import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { DatePicker } from '#components/form/DatePicker.tsx';
import { TimePicker } from '#components/form/TimePicker.tsx';

import { composeDateTimeValue, dateFieldToJSDate } from './timeWindowFields.ts';

type TimeWindowField = 'startDate' | 'startTime' | 'endDate' | 'endTime';

type TimeWindowFormProps = {
    allowPastDates?: boolean;
    minDate?: Date;
    prefix?: string;
};

const TIME_WINDOW_GRID_TEMPLATE_COLUMNS = 'minmax(0, 1fr) 140px';

const getTimeWindowFieldName = (prefix: string | undefined, field: TimeWindowField) =>
    prefix ? `${prefix}.${field}` : field;

export const TimeWindowForm = ({ allowPastDates = false, minDate, prefix }: TimeWindowFormProps) => {
    const intl = useIntl();
    // biome-ignore lint/suspicious/noExplicitAny: shared form view supports order and stop-edit form shapes.
    const { watch, setValue, getFieldState, formState, control } = useFormContext<any>();

    const startDateName = getTimeWindowFieldName(prefix, 'startDate');
    const startTimeName = getTimeWindowFieldName(prefix, 'startTime');
    const endDateName = getTimeWindowFieldName(prefix, 'endDate');
    const endTimeName = getTimeWindowFieldName(prefix, 'endTime');

    const startDate = watch(startDateName);
    const startTime = watch(startTimeName);
    const endDate = watch(endDateName);
    const endTime = watch(endTimeName);

    const setTimeWindowFieldValue = useCallback(
        (field: TimeWindowField, value: string) => {
            setValue(getTimeWindowFieldName(prefix, field), value, { shouldValidate: true, shouldDirty: true });
        },
        [prefix, setValue]
    );

    useTimeWindowSync({
        startDate,
        startTime,
        endDate,
        endTime,
        setFieldValue: setTimeWindowFieldValue,
    });

    const parsedStartDate = dateFieldToJSDate(startDate);
    const startTimeError = getFieldState(startTimeName, formState).error?.message;
    const endTimeError = getFieldState(endTimeName, formState).error?.message;
    const timeLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.time',
        defaultMessage: 'Time',
    });

    return (
        <div className='display-grid grid-cols-1 gap-10'>
            <div className='display-grid gap-10' style={{ gridTemplateColumns: TIME_WINDOW_GRID_TEMPLATE_COLUMNS }}>
                <DatePicker
                    control={control}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.from',

                        defaultMessage: 'From',
                    })}
                    name={startDateName}
                    className='margin-bottom-0'
                    allowPastDates={allowPastDates}
                    minDate={minDate}
                    fieldValueMode='date'
                />
                <TimePicker
                    label={timeLabel}
                    name={startTimeName}
                    value={startTime}
                    onChange={value => setTimeWindowFieldValue('startTime', value)}
                    error={startTimeError}
                    className='margin-bottom-0'
                />
            </div>

            <div className='display-grid gap-10' style={{ gridTemplateColumns: TIME_WINDOW_GRID_TEMPLATE_COLUMNS }}>
                <DatePicker
                    control={control}
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.orders.create-dialog.location.to',

                        defaultMessage: 'To',
                    })}
                    name={endDateName}
                    className='margin-bottom-0'
                    allowPastDates={allowPastDates}
                    minDate={parsedStartDate ?? minDate}
                    fieldValueMode='date'
                />
                <TimePicker
                    label={timeLabel}
                    name={endTimeName}
                    value={endTime}
                    onChange={value => setTimeWindowFieldValue('endTime', value)}
                    error={endTimeError}
                    className='margin-bottom-0'
                />
            </div>
        </div>
    );
};

const useTimeWindowSync = ({
    startDate,
    startTime,
    endDate,
    endTime,
    setFieldValue,
}: {
    startDate?: string;
    startTime?: string;
    endDate?: string;
    endTime?: string;
    setFieldValue: (field: TimeWindowField, value: string) => void;
}) => {
    const previousDatesRef = useRef({ startDate, endDate });

    useEffect(() => {
        const previousDates = previousDatesRef.current;
        const startDateWasAdded = Boolean(startDate && !previousDates.startDate);
        const endDateWasAdded = Boolean(endDate && !previousDates.endDate);

        const setIfChanged = (field: TimeWindowField, currentValue: string | undefined, nextValue: string) => {
            if ((currentValue ?? '') !== nextValue) {
                setFieldValue(field, nextValue);
            }
        };

        if (!startDate) {
            setIfChanged('startTime', startTime, '');
        }

        if (!endDate) {
            setIfChanged('endTime', endTime, '');
        }

        if (startDate && startDateWasAdded && !endDate) {
            setFieldValue('endDate', startDate);
        }

        if (endDate && endDateWasAdded && !startDate) {
            setFieldValue('startDate', endDate);
        }

        if (startDate && endDate && startDate === endDate) {
            if (startTime && !endTime) {
                setFieldValue('endTime', startTime);
            }

            if (endTime && !startTime) {
                setFieldValue('startTime', endTime);
            }
        }

        if (startDate && endDate) {
            const startDateTime = composeDateTimeValue(startDate, startTime);
            const endDateTime = composeDateTimeValue(endDate, endTime);

            if (startDateTime && endDateTime && startDateTime > endDateTime) {
                setIfChanged('endDate', endDate, startDate);
                setIfChanged('endTime', endTime, startTime ?? '');
            }
        }

        previousDatesRef.current = { startDate, endDate };
    }, [startDate, startTime, endDate, endTime, setFieldValue]);
};
